import { useState, useMemo } from "react";
import { Category, NewTask } from "../types";
import useStatusCategories from "./newUseStatusCategories";
import { Contact } from "../../../shared/orderInfo/types";
import { EventType, StatusCategory } from "../../types";

export const useTaskFilters = (tasks: NewTask[], contacts: Contact[], eventTypes: EventType[]) => {
  const [statusFilters, setStatusFilters] = useState<StatusCategory[]>([]);
  const [activityFilters, setActivityFilters] = useState<EventType[]>([]);
  const [assignedToFilters, setAssignedToFilters] = useState<string[]>([]);
  const [associationFilters, setAssociationFilters] = useState<Contact[]>([]);
  const [taskFilter, setTaskFilter] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [taskQuickNoteFilter, setTaskQuickNoteFilter] = useState("");
  const [hideCompleted, setHideCompleted] = useState<boolean>(false);
  const [categoryFilters, setCategoryFilters] = useState<Category[]>([]);

  const [filtersVisible, setFiltersVisible] = useState(false);
  const { statusCategories } = useStatusCategories();

  const associationFilterOptions = useMemo(() => {
    const taskContactCodes = tasks.map((t) => t.contactCode);
    return contacts.filter((c) => taskContactCodes.includes(c.code));
  }, [tasks, contacts]);

  const assignedToFilterOptions = useMemo(() => {
    if (!tasks?.length) return [];
    return [...new Set(tasks.map((t) => t.assignedToName).filter(Boolean))];
  }, [tasks]);

  const activityFilterOptions = useMemo(() => {
    if (!tasks?.length) return [];
    return eventTypes.filter((e) =>
      tasks.some((t) => {
        const latestTask = t.updateHistories ? t.updateHistories[t.updateHistories.length - 1] : null;
        return latestTask && e.id === latestTask.eventType;
      })
    );
  }, [tasks, eventTypes]);

  const filterTask = (task: NewTask) => {
    const statusCategory = statusCategories.find((s) => s.id === task.currentOmitOption?.statusApplicableTo);

    const isPassTaskFilter =
      ((hideCompleted && statusCategory && !["Completed", "N/A"].includes(statusCategory.description)) ||
        !hideCompleted) &&
      (!taskFilter ||
        task.description.toLowerCase().includes(taskFilter.toLowerCase()) ||
        task.parentTaskTag?.toLowerCase()?.includes(taskFilter.toLowerCase()));

    const filterCategoryIds = categoryFilters.map((s) => s.id);
    const isPassCategoryFilter =
      !filterCategoryIds?.length || (task.categoryId && filterCategoryIds.includes(task.categoryId));

    const isPassTaskQuickNote =
      !taskQuickNoteFilter || task.quickNote?.toLowerCase().includes(taskQuickNoteFilter.toLowerCase());

    const filterStatusIds = statusFilters.map((s) => s.id);
    const isPassStatusFilter =
      !filterStatusIds?.length || filterStatusIds.includes(task.currentOmitOption?.statusApplicableTo);

    const filterContactCodes = associationFilters.map((a) => a.code);
    const isPassAssociationFilters = !filterContactCodes?.length || filterContactCodes.includes(task.contactCode);

    const activityFiltersIds = activityFilters.map((a) => a.id);
    const latestTaskEventId = task.updateHistories[task.updateHistories?.length - 1]?.eventType;
    const isPassActivityFilter = !activityFiltersIds?.length || activityFiltersIds.includes(latestTaskEventId);

    const isPassAssignedToFilter = !assignedToFilters?.length || assignedToFilters.includes(task.assignedToName);

    const isTaskPassing =
      isPassTaskFilter &&
      isPassStatusFilter &&
      isPassAssociationFilters &&
      isPassActivityFilter &&
      isPassAssignedToFilter &&
      isPassTaskQuickNote &&
      isPassCategoryFilter;

    return isTaskPassing ? task : null;
  };

  let filteredSortedTasks = tasks.reduce((passedTasks: NewTask[], task) => {
    const filteredTask = filterTask(task);
    if (filteredTask) {
      passedTasks.push(filteredTask);
    }
    return passedTasks;
  }, []);

  return {
    statusFilters,
    setStatusFilters,
    activityFilters,
    setActivityFilters,
    assignedToFilters,
    setAssignedToFilters,
    associationFilters,
    setAssociationFilters,
    taskFilter,
    setTaskFilter,
    categoryFilter,
    setCategoryFilter,
    taskQuickNoteFilter,
    setTaskQuickNoteFilter,
    associationFilterOptions,
    assignedToFilterOptions,
    activityFilterOptions,
    filtersVisible,
    setFiltersVisible,
    filteredSortedTasks,
    hideCompleted,
    setHideCompleted,
    categoryFilters,
    setCategoryFilters
  };
};
